import { useEffect, useState, type ReactNode } from 'react'
import { describeClass } from '../api/classDescription'
import ConfirmAction from './ConfirmAction'
import { Link, useSearchParams } from 'react-router-dom'
import AttendancePanel from './AttendancePanel'
import BankedInHand from './BankedInHand'
import ReportAbsence from './ReportAbsence'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Check,
  ChevronDown,
  MessageCircle,
} from 'lucide-react'
import { clockTime, fetchBankedFor, meridiem } from '../api/attendance'
import { fetchContent, fill, text, type ContentMap } from '../api/content'
import { fetchDancerLogins } from '../api/portal'
import { useCan, useSession } from '../useSession'
import { AskForm } from './PrivateRequestsPanel'
import AsksOnHome from './AsksOnHome'
import type { AskPart } from '../api/asks'
import ScheduleHeader from './ScheduleHeader'
import { PracticeLinks } from './PracticeLinks'
import PaymentLine from './PaymentLine'
import { formatFullDate, monthGrid, monthOf, today } from '../api/calendar'
import {
  byDate,
  bookMakeup,
  cancelMakeup,
  cancelPrivate,
  fetchBankableSessions,
  bankSession,
  type BankableSession,
  fetchMakeupOffer,
  fetchPortalDancer,
  fetchPortalHome,
  repairableOf,
  scheduleWindow,
  stepAnchor,
  setTShirtSize,
  type DancerStanding,
  type MakeupCandidate,
  type MakeupOffer as MakeupOfferData,
  type MissedSession,
  type PortalDancerView,
  type PortalHome,
  type PortalSession,
  isScheduleView,
  type ScheduleView,
} from '../api/portal'

/** The whole household, rather than one dancer (D-176). */
const ALL = 'all'

/**
 * What a family sees when they sign in.
 *
 * **Not the studio calendar with a filter on it.** A parent opens this to answer
 * one question — where does my dancer need to be, and when — so it leads with
 * her next class rather than a week grid built for a teacher.
 *
 * **Built for competitive families**, who are the ones who need it: standing,
 * rehearsals and absences are comp concerns, while a rec family has one class on
 * one night and almost none of this. That shapes what leads.
 *
 * The tab bar carries a slot for messaging (M3). It is shown as coming rather
 * than as a dead button — a tab that does nothing collects bug reports that cost
 * pilot feedback.
 */
export default function PortalView({
  showGreeting = true,
  /**
   * Which sides of the ask list this portal draws (D-543, D-767).
   *
   * Both by default, because a family's Home is the only list it has. Home
   * narrows it to `requester` for an account that also teaches: the teaching
   * half above it already draws everything waiting on her, and drawing it
   * twice is what a dual-role account saw.
   */
  askParts = ['owner', 'requester'],
}: { showGreeting?: boolean; askParts?: readonly AskPart[] } = {}) {
  const [dancerId, setDancerId] = useState<string | null>(null)

  /**
   * ⚠ **The ask list is held behind the *money* switch, and that is wider
   * than it will stay** (D-920).
   *
   * The list mixes kinds: answering which class a makeup repairs is free and
   * is hers by D-438, while saying yes to a teacher's private commits the
   * household. The right answer is per kind, decided where the kind is known
   * — and until that lands, holding the whole list behind the paid switch is
   * the safe approximation, because the alternative shows an
   * attendance-only dancer a live *Yes* on a paid lesson.
   *
   * **The cost is named rather than hidden**: a dancer granted attendance
   * alone cannot answer her own makeup-claim ask, which D-438 says she may.
   * `docs/GAPS.md`.
   */
  const mayAct = useCan('BookPrivates')

  const { data: home, isPending, isError } = useQuery({
    queryKey: ['portal'],
    queryFn: fetchPortalHome,
  })

  // Whoever comes first, until a parent picks. A household of one never sees
  // the switcher at all.
  useEffect(() => {
    if (!dancerId && home?.dancers.length) setDancerId(home.dancers[0].dancerId)
  }, [home, dancerId])

  const { data: dancer } = useQuery({
    queryKey: ['portal-dancer', dancerId],
    queryFn: () => fetchPortalDancer(dancerId!),
    enabled: dancerId !== null && dancerId !== ALL,
  })

  if (isPending) return <p className="px-5 py-10 text-center text-[14px] text-ink-3">Loading...</p>

  if (isError || !home) {
    return (
      <p className="px-5 py-10 text-center text-[14px] text-crit">
        Could not load your account. Please try again.
      </p>
    )
  }

  if (home.dancers.length === 0) {
    return (
      <div className="mx-auto w-full max-w-[30rem] px-5 py-12 text-center">
        <h1 className="font-display text-xl">Nothing here yet</h1>
        <p className="mt-2 text-[14px] text-ink-3">
          We could not find any dancers on your account. Please get in touch with the studio.
        </p>
      </div>
    )
  }

  return (
    /*
      Phone-first, and the phone layout is the reference rather than the
      fallback (D-176). Most parents will use this on a handset, so the wide
      view is the same components given room — not a second design the phone
      degrades into. 30rem stays the measure on small screens; it only widens
      once there is space the content can actually use.
    */
    <div className="flex min-h-full flex-col">
      <div className="flex-1">
        <header className="mb-4 flex flex-wrap items-baseline justify-between gap-x-6 gap-y-3">
          {/*
            Suppressed on Home, where the page already has a greeting at the top
            and a second one halfway down reads as the start of another screen
            (D-380). Still shown wherever this renders on its own, which is what
            keeps it the page heading rather than decoration.
          */}
          {showGreeting && <h1 className="font-display text-xl">Hi, {home.greeting}</h1>}

          {/*
            On a laptop this reads as site navigation, like every other screen.
            On a phone it is hidden and the bottom bar takes over — a thumb
            reaches the bottom of a handset and not the top (D-176).

            Two renderings of one state rather than two navigation models: the
            tab is still the same `tab`, so nothing can disagree about which
            screen is showing.
          */}
        </header>

        {/*
          Tier 1, above everything, on every tab (D-297, D-422, D-543, D-553).

          A teacher offered a different time for a private, or marked a lesson
          a makeup and only the family can say which class it repairs — until
          they answer, nothing happens, so it cannot sit behind a tab or behind
          the right child being selected. It lived inside the private-lessons
          panel once, found only by picking the right dancer, opening Schedule
          and scrolling past two other panels (D-288). The row is answered
          here, the same row a teacher answers on her own list.

          <b>Both sides, because this is the only list a family has</b> (gate
          Q2): what they are waiting on — the private still with the teacher —
          sits under what is waiting on them, with the way to take it back.

          <b>Unless she also teaches</b> (D-767). Home draws both portals for a
          dual-role account, and the teaching half already answers *what is
          waiting on you* — so the same row appeared twice, once under each
          heading, with the same `id` on both sections. Which sides show is the
          screen's choice, which is why it arrives as a prop rather than being
          decided here.
        */}
        {/*
          ⚠ **Withheld from a reader who may not act** (D-918).

          These rows are not a record. A family ask carries *Yes* and *No*, and
          `AccountsOfFamilyAsync` deliberately counts a dancer's own login as
          the family (D-438, D-539, D-562) — so the buttons **work**: accepting
          a counter-offered time commits the household to a paid lesson,
          withdrawing takes back her parent's request, answering a makeup claim
          changes the attendance credit her own standing is computed from.

          Before this screen was hers she never saw them. Hiding them puts that
          back rather than inventing a boundary — **and the server still lets
          her answer**, because the ask doors are on `ViewSchedule` like the
          rest of the envelope. That half is a `docs/GAPS.md` row, said out
          loud here because *a screen that does not offer it is not a
          boundary* is the whole argument D-918 makes.
        */}
        {mayAct && <AsksOnHome parts={askParts} />}

        {/*
          Home draws its own; the Schedule tab gets one from the panel, which
          owns it so that `/schedule` has it too.

          **Only on the two tabs it changes** (D-257) — that rule is unchanged
          and it is now enforced in two places rather than one condition.
          Account shows the whole household and News is the studio's, so on
          those the row rendered, highlighted a name and did nothing, reported
          from the pilot as "clicking them doesn't do anything". A switcher that
          moves a highlight and changes no content is a control that lies with a
          coat of paint on it.

          The rest of why it exists — multi-dancer by default, and `flex-1` only
          at phone width — lives on `DancerPicker` itself now.
        */}
        {/*
          No *Everyone* here (D-388). It rendered each dancer's next class side
          by side, which is what the Today panel above now says one row per
          person — the same answer twice on one screen, and reported as such.
          Schedule keeps it, because there it is a combined week rather than a
          list of next classes.
        */}
        <DancerPicker
          dancers={home.dancers}
          value={dancerId}
          onChange={setDancerId}
          includeEveryone={false}
        />

        {dancer && <HomeTab dancer={dancer} />}
      </div>

    </div>
  )
}

function HomeTab({ dancer }: { dancer: PortalDancerView }) {

  return (
    <>
      {/*
        The "Next class" card is gone (D-380).

        It named one child's next session in a coloured panel — which is the
        Today panel's first row, said once for the whole family rather than
        once per dancer tab. Its <code>NothingScheduled</code> fallback is
        covered too: a quiet row says "nothing today, next Monday", including
        past the fortnight this payload carries.
      */}

      {/*
        Two columns once there is room, and one on a phone (D-176). What is
        coming leads and standing sits beside it — she opens this to find out
        where her dancer needs to be, and checks the 80% while she is here.
        Stacked, standing would push "coming up" below the fold on a laptop for
        no reason other than source order.
      */}
      {/*
        Her fortnight is gone from Home (D-380). The Today panel above names
        today for every child at once, and the calendar draws the rest — this
        was the second implementation of it.

        <b>Standing stays.</b> It is a fact about her, not a schedule, and it is
        why a parent opens this at all when nothing is on tonight.
      */}
      <div className="mt-4">
        <Standing dancer={dancer} />
      </div>

      <Sizes dancer={dancer} />
    </>
  )
}

/**
 * What the studio has on file for costumes (D-238).
 *
 * <b>Here rather than behind a tab of its own.</b> A parent already taps a child
 * to get to this screen, and sizes are looked up a handful of times a year —
 * around costume ordering — not weekly. A fifth tab would be permanent furniture
 * for an occasional question. Last on the page for the same reason: it is the
 * thing she scrolls to deliberately, never the thing she opened the app for.
 *
 * <b>Why it exists at all.</b> Every "what size did you put her down as?" went
 * through Tonya, and her time is the scarcest thing in this project. A family
 * reading their own child's record removes a message without costing anything.
 *
 * <b>Folded shut until asked for (D-256).</b> Eleven rows in two columns is the
 * tallest thing on the screen, and on a handset it pushed the next class — the
 * thing she opened the app for — off the bottom. The same reasoning that put it
 * last says it should also be closed: a few times a year is not often enough to
 * earn a permanent third of the page.
 *
 * <b>The t-shirt size rides on the summary line</b>, because it is the one
 * figure a parent is asked for and the only one she can change. Answering the
 * common question without a tap is worth more than the tidiness of hiding
 * everything equally.
 */
function Sizes({ dancer }: { dancer: PortalDancerView }) {
  const queryClient = useQueryClient()
  const m = dancer.measurements

  /*
   * **Reading her sizes is hers; changing one is an act** (D-918). A dancer's
   * own login sees this panel and cannot edit it until a parent says
   * otherwise — and the server refuses either way, so this is about not
   * offering a control that would be refused, which is worse than no control.
   */
  const mayAct = useCan('ArrangeAttendance')

  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(m.tShirt ?? '')
  const [problem, setProblem] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: () => setTShirtSize(dancer.dancerId, draft.trim() || null),
    onSuccess: () => {
      setEditing(false)
      setProblem(null)
      queryClient.invalidateQueries({ queryKey: ['portal-dancer'] })
    },
    onError: (e) => setProblem((e as Error).message),
  })

  // Garment sizes and body measurements read differently and are used for
  // different things, so they are two lists rather than one long one.
  const garments = [
    ['T-shirt', m.tShirt],
    ['Leotard', m.leotard],
    ['Pants', m.pant],
    ['Shoes', m.shoe],
    ['Tights', m.tights],
  ] as const

  // "Bust" and "Hip" are the words the studio uses; the columns behind them are
  // Chest and Hips (D-217's naming note).
  const body = [
    ['Height', m.height],
    ['Waist', m.waist],
    ['Girth', m.girth],
    ['Bust', m.chest],
    ['Inseam', m.inseam],
    ['Hip', m.hips],
  ] as const

  return (
    <details
      className="group mt-4 rounded-2xl border border-line bg-surface px-4 py-3.5"
      /*
       * Reopening on its own when a parent starts editing, so that "Change
       * t-shirt size" cannot leave a live form inside a shut box — the state
       * outlives the disclosure otherwise, and the next tap on the summary
       * would reveal a half-filled field she had forgotten about.
       */
      open={editing || undefined}
    >
      {/*
        The marker is replaced by the chevron rather than shown beside it. Two
        affordances for one control read as two controls.
      */}
      <summary className="flex cursor-pointer list-none flex-wrap items-center gap-x-3 gap-y-1 py-1.5 [&::-webkit-details-marker]:hidden">
        <h2 className="text-[11px] font-bold tracking-[0.11em] text-ink-3 uppercase">
          Costume sizes
        </h2>

        {/* The answer to the question this panel exists for, without a tap. */}
        {m.tShirt && (
          <span className="rounded-lg bg-surface-2 px-2 py-0.5 text-[12px] font-medium wrap-anywhere">
            T-shirt {m.tShirt}
          </span>
        )}

        {m.measuredOn && (
          /*
           * The date, always, when there is one.
           *
           * A nine-year-old's girth moves a great deal in a season and a figure
           * with no date reads as current however old it is — this is what tells
           * a parent to ask for a re-measure rather than assume.
           */
          <span className="text-[12px] text-ink-3">Measured {formatFullDate(m.measuredOn)}</span>
        )}

        <ChevronDown
          size={16}
          strokeWidth={2.5}
          aria-hidden="true"
          className="ml-auto shrink-0 text-ink-3 transition-transform group-open:rotate-180"
        />
      </summary>

      {m.isEmpty && !editing ? (
        <p className="mt-2 text-[13.5px] leading-relaxed text-ink-3">
          The studio has not recorded {dancer.name.split(' ')[0]}&rsquo;s sizes yet. They are
          usually taken in the studio before costumes are ordered.
        </p>
      ) : (
        <div className="mt-3 grid gap-x-8 gap-y-4 sm:grid-cols-2">
          <SizeList label="Clothing" rows={garments} />
          <SizeList label="Measurements" rows={body} />
        </div>
      )}

      {/*
        One editable field, and it says why the others are not.

        Without the sentence, "why can I change this box and not that one?" is
        the message this screen was built to prevent.
      */}
      <div className="mt-4 border-t border-line pt-3.5">
        {editing ? (
          <div className="flex flex-wrap items-end gap-2.5">
            <label className="flex-1">
              <span className="mb-1 block text-[12px] font-semibold text-ink-2">T-shirt size</span>
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                autoFocus
                maxLength={40}
                placeholder="YL, Adult S&hellip;"
                className="w-full rounded-lg border border-line-2 bg-surface-2 px-3 py-2.5 text-[14px] outline-none focus:border-accent"
              />
            </label>

            <button
              type="button"
              disabled={save.isPending}
              onClick={() => save.mutate()}
              className="rounded-lg bg-accent px-4 py-2.5 text-[14px] font-semibold text-on-accent disabled:opacity-60"
            >
              {save.isPending ? 'Saving...' : 'Save'}
            </button>

            <button
              type="button"
              onClick={() => {
                setEditing(false)
                setDraft(m.tShirt ?? '')
                setProblem(null)
              }}
              className="rounded-lg border border-line-2 px-4 py-2.5 text-[14px] font-semibold text-ink-2"
            >
              Cancel
            </button>
          </div>
        ) : (
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12.5px] leading-relaxed text-ink-3">
            {/*
              ⚠ **A whole sentence per branch.** Assembled around a conditional
              clause, the reader who may not act got *"The studio takes these
              with a tape measure. ask the studio if anything else looks
              wrong."* — a sentence starting lower-case, on the screen of the
              person this change was for. An audit found it by reading; the
              test over this very branch was green because it asserted only
              that the button was absent.
            */}
            <span>
              {mayAct
                ? 'The studio takes these with a tape measure. You can change the t-shirt size — ask the studio if anything else looks wrong.'
                : 'The studio takes these with a tape measure. Ask the studio if anything looks wrong.'}
            </span>

            {/*
              `py-1` for the 24px tap target, not for the look. It measured
              105x20 — under the floor `audit.mjs` enforces — and went unseen
              because the audit had never rendered this screen with a family
              signed in (D-255).
            */}
            {mayAct && (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="py-1 font-semibold text-accent underline underline-offset-2"
              >
                {m.tShirt ? 'Change t-shirt size' : 'Add a t-shirt size'}
              </button>
            )}
          </p>
        )}

        {problem && (
          <p role="alert" className="mt-2 text-[13px] text-crit">
            {problem}
          </p>
        )}
      </div>
    </details>
  )
}

function SizeList({
  label,
  rows,
}: {
  label: string
  rows: readonly (readonly [string, string | null])[]
}) {
  return (
    <div>
      <p className="mb-1.5 text-[12px] font-semibold text-ink-2">{label}</p>

      <dl className="text-[13.5px]">
        {rows.map(([name, value]) => (
          <div key={name} className="flex justify-between gap-3 border-b border-line py-1.5 last:border-0">
            <dt className="text-ink-3">{name}</dt>

            {/* An em dash rather than a blank: "not recorded" is an answer, and
                an empty cell reads as a rendering fault. */}
            <dd className={value ? 'font-medium wrap-anywhere' : 'text-ink-3'}>{value ?? '—'}</dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

/**
 * A class she can take now and spend later (D-611).
 *
 * **Its own card, and the difference is the sentence rather than the layout.**
 * A makeup card names the absence it repairs; this one cannot, because there is
 * none yet. Reusing the makeup card with that line blanked would leave a row
 * that reads as a makeup for something unnamed.
 */
function BankableSessionCard({
  session,
  content,
  onBank,
  banking,
}: {
  session: BankableSession
  content: ContentMap | undefined
  onBank: (session: BankableSession) => void
  /** The occurrence being booked right now, or null. */
  banking: string | null
}) {
  const short = session.minimumSignups !== null && session.signups < session.minimumSignups
  const mayAct = useCan('ArrangeAttendance')

  return (
    <li className="w-full rounded-lg border border-line-2 p-2">
      {/* Wrapped, never truncated (D-88): the part that says which class it is
          sits at the end of a machine-shaped name. */}
      <div className="wrap-anywhere text-[13px] font-semibold text-ink">{session.className}</div>
      <div className="text-[12.5px] text-ink-3">
        {formatFullDate(session.date)}
        {session.startTime ? ` · ${clockTime(session.startTime)}` : ''}
        {session.roomName ? ` · ${session.roomName}` : ''}
      </div>
      {session.instructors.length > 0 && (
        <div className="wrap-anywhere text-[12.5px] text-ink-3">
          {session.instructors.join(', ')}
        </div>
      )}

      {/* Said before she taps, never after (D-403). A button that silently
          turns into a request is the fault one step later. */}
      {session.needsApproval && (
        <div className="mt-1 text-[12.5px] text-ink-2">
          {text(
            content,
            'portal.bank.needsApproval',
            'A level up from her own class, so her teacher has to say yes first.',
          )}
        </div>
      )}

      {short && (
        <div className="mt-1 text-[12.5px] text-ink-2">
          {fill(
            content,
            'portal.bank.stillNeeded',
            { stillNeeded: (session.minimumSignups ?? 0) - session.signups },
            'Needs {{stillNeeded}} more to run.',
          )}
        </div>
      )}

      <div className="mt-2">
        {session.alreadyBooked ? (
          <span className="text-[12.5px] font-semibold text-good">
            {text(content, 'portal.bank.booked', 'Banked. She has a place on this one.')}
          </span>
        ) : (
          // Offered only to somebody who may actually take it (D-918).
          mayAct && (
            <button
              type="button"
              disabled={banking !== null}
              onClick={() => onBank(session)}
              className="min-h-[44px] rounded-lg border border-line-2 px-3 text-[12.5px] font-semibold text-ink-2 hover:border-ink-3 disabled:opacity-50"
            >
              {banking === session.occurrenceId
                ? text(content, 'portal.bank.booking', 'Booking...')
                : text(content, 'portal.bank.book', 'Bank this class')}
            </button>
          )
        )}
      </div>
    </li>
  )
}

/**
 * One session she could attend, and the button that takes it (D-283).
 *
 * <b>One card, because there are two lists now.</b> The general offer shows
 * everything she may book; a missed row shows what repairs *that* absence
 * (D-342). Rebuilding this for the second caller is the drift D-199 names -
 * two implementations of one idea, the second never receiving the first fixes.
 * The one-off state (D-279, D-282) and the disabled-button reasoning live here
 * once.
 *
 * <b>The absence it repairs is passed in, not read off the candidate.</b> From
 * the general list that is the label - the oldest it can repair; from a missed
 * row it is the absence she actually chose. Same card, different answer to
 * *which gap does this mend* (D-340).
 */
function MakeupCandidateCard({
  candidate,
  content,
  repairs,
  onBook,
  booking,
}: {
  candidate: MakeupCandidate
  content: ContentMap | undefined
  repairs: string
  onBook: (candidate: MakeupCandidate, repairs: string) => void
  /** The occurrence id being booked right now, or null. */
  booking: string | null
}) {
  const mayAct = useCan('ArrangeAttendance')

  return (
    <li
      className="w-full rounded-lg border border-line-2 p-2"
    >
      {/*
        Wrapped, never truncated (D-88). `COMP|REC_TECH_ BALLET_2NEW
        | MO 600 | MAC` is one unbreakable word to CSS, and the part
        that says which class it is sits at the end.
      */}
      <div className="wrap-anywhere text-[13px] font-semibold text-ink">
        {candidate.className}
      </div>
      <div className="text-[12.5px] text-ink-3">
        {formatFullDate(candidate.date)}
        {candidate.startTime ? ` · ${clockTime(candidate.startTime)}` : ''}
        {candidate.roomName ? ` · ${candidate.roomName}` : ''}
      </div>
      {candidate.instructors.length > 0 && (
        <div className="wrap-anywhere text-[12.5px] text-ink-3">
          {candidate.instructors.join(', ')}
        </div>
      )}

      {/*
        Where a one-off stands (D-279, D-282).

        A class already running says nothing here — it runs whoever
        turns up, and a badge on every row is the noise D-177
        stripped out. Only a session that has to fill speaks.

        The count is derived on every read, so this is current
        rather than as-of-some-refresh: the third signup confirms
        it for all three families at once.
      */}
      {/*
        **A level up, and what it costs** (D-403).

        The offer used to drop these entirely (D-401), because a Book button on
        something the server refuses is worse than no offer. It carries the
        condition now, and the card has to say so *before* she taps — a button
        that silently turns into a request is the same fault one step later.
      */}
      {candidate.needsApproval && (
        <div className="mt-1 text-[12.5px] text-ink-2">
          {text(
            content,
            'portal.makeup.needsApproval',
            'A level up from her own class, so her teacher has to say yes first.',
          )}
        </div>
      )}

      {candidate.isOneOff && (
        <div className="mt-1 text-[12.5px] text-ink-2">
          {candidate.isConfirmed
            ? text(
                content,
                'portal.makeup.oneOffConfirmed',
                'This one is going ahead.',
              )
            : fill(
                content,
                'portal.makeup.oneOffProvisional',
                { stillNeeded: candidate.stillNeeded },
                'Needs {{stillNeeded}} more to run.',
              )}
        </div>
      )}

      {/*
        And she can actually take it (D-283).

        The list said "needs one more to run" for a while with no
        way to be that one — information ahead of the ability to
        act on it.

        <b>Every button disables; only one says so.</b> There is a
        single entitlement behind the whole list, so a request in
        flight really does bar all of them - but labelling all of
        them <i>Booking...</i> tells a parent the studio is
        booking four classes at once.
      */}
      {/* Offered only to somebody who may actually take it (D-918). */}
      {mayAct && (
        <div className="mt-2">
          <button
            type="button"
            disabled={booking !== null}
            onClick={() => onBook(candidate, repairs)}
            className="rounded-lg border border-line-2 px-3 py-1.5 text-[12.5px] font-semibold text-ink-2 hover:border-ink-3 disabled:opacity-40"
          >
            {booking === candidate.occurrenceId
              ? text(content, 'portal.makeup.booking', 'Booking...')
              : candidate.needsApproval
                ? text(content, 'portal.makeup.ask', 'Ask her teacher')
                : text(content, 'portal.makeup.book', 'Book this')}
          </button>
        </div>
      )}
    </li>
  )
}

/**
 * Standing, shown only to the families it applies to.
 *
 * A rec dancer is measured but never gated (PRD §7.6) — putting a threshold in
 * front of one would invent a rule the studio does not have.
 */
/**
 * Which days were missed, on demand (D-271).
 *
 * <b>The question a percentage cannot answer.</b> "0 of 3" tells a parent
 * something is wrong and nothing about what to do next; the owner's report was
 * *"it would be nice if I could click on it and see what dates were marked
 * absent"*, which is the same observation from the other side.
 *
 * <b>Collapsed by default, and one row when collapsed.</b> This sits on a phone
 * under everything else on the home screen, so it earns its space only when
 * tapped — and a family with nothing missed sees a single reassuring line
 * rather than an empty container.
 *
 * <b>The count comes from the standing, never from the list.</b> An excused
 * absence is excluded from the percentage entirely (PRD §7.6) and the list is
 * capped, so counting rows would print a number that disagrees with the bar
 * directly above it. Away days are still listed — a parent who reported one
 * wants to see it landed — they are just not counted as misses.
 */
/**
 * Repairing one absence, from the row that names it (D-342).
 *
 * **The question a parent has is "what can Ellie do about Tuesday?"** — not
 * "you are owed two makeups, pick which absence to spend". The second is the
 * studio's accounting, and D-297 already said whether a repair is a drop-in or
 * a private is *our* distinction rather than hers.
 *
 * **Every sentence here is about this class.** "No makeups available" would be
 * a claim about her entitlement built on a fact about one class, and she may
 * have a perfectly good repair waiting for a different absence (D-209).
 */
function RepairRow({
  missed,
  dancerId,
  open,
  onOpen,
  offer,
  pending,
  failed,
}: {
  missed: MissedSession
  dancerId: string
  open: boolean
  onOpen: () => void
  offer?: MakeupOfferData
  pending: boolean
  failed: boolean
}) {
  const { data: content } = useQuery({ queryKey: ['content'], queryFn: fetchContent })
  const queryClient = useQueryClient()

  /*
   * Booking from here books the absence *she named*, which is the whole reason
   * this row exists. The general list defaults to the oldest (D-276); a parent
   * who tapped one row has already chosen.
   *
   * The same keys the general offer invalidates, so a booking taken here
   * refreshes the panel above it and the offer beside it (D-340).
   */
  const book = useMutation({
    mutationFn: ({ candidate, absence }: { candidate: MakeupCandidate; absence: string }) =>
      bookMakeup(dancerId, candidate.occurrenceId, absence),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-makeup', dancerId] })
      queryClient.invalidateQueries({ queryKey: ['portal-dancer', dancerId] })
    },
  })

  if (!open) {
    return (
      <button
        type="button"
        onClick={onOpen}
        className="mt-1 -mx-1 rounded-md px-1 py-1 text-[12px] font-semibold text-accent hover:underline"
      >
        {text(content, 'portal.makeup.findOne', 'Find a makeup')}
      </button>
    )
  }

  if (pending) {
    return <p className="mt-1 text-[12px] text-ink-3">Checking&hellip;</p>
  }

  /*
   * A failed lookup never becomes a statement about the data (D-209) — every
   * database-backed endpoint refuses for about 75 seconds after a deploy, so
   * this is the ordinary case rather than the rare one.
   */
  if (failed || !offer) {
    return (
      <p className="mt-1 text-[12px] text-crit">
        Could not check for makeup classes just now. Please try again.
      </p>
    )
  }

  /*
   * **Filtered on the candidate's own set, not on its label** (D-340). The
   * label names one absence per session so a row has a line to print; filtering
   * on it answers for that one absence and finds nothing for every other.
   */
  const repairs = offer.candidates.filter((c) =>
    c.repairsOccurrenceIds.includes(missed.occurrenceId),
  )

  if (repairs.length === 0) {
    return (
      <p className="mt-1 text-[12px] text-ink-3">
        {text(content, 'portal.makeup.nothingForThisClass', 'Nothing matches this class just now.')}
      </p>
    )
  }

  return (
    <ul className="mt-1 flex flex-col gap-1.5">
      {repairs.map((c) => (
        <MakeupCandidateCard
          key={c.occurrenceId}
          candidate={c}
          content={content}
          /*
            The absence she actually chose, not the candidate label. From here
            she pointed at one row and said *repair this* - booking the oldest
            instead would mend a different gap than the one she tapped (D-340).
          */
          repairs={missed.occurrenceId}
          onBook={(candidate, absence) => book.mutate({ candidate, absence })}
          booking={book.isPending ? book.variables?.candidate.occurrenceId ?? null : null}
        />
      ))}

      {book.error && (
        <li role="alert" className="text-[12px] text-crit">
          {(book.error as Error).message}
        </li>
      )}
    </ul>
  )
}

/**
 * A parent's controls for one absence, handed to the shared panel (D-589).
 *
 * <b>The panel is one component and this is what makes it the parent's</b>:
 * the makeup finder for a technique class and the private request for a
 * routine (D-405), rendered under the night they repair, only where nothing
 * is repairing it yet. The studio's copy of the panel passes nothing here and
 * sees the same nights as facts.
 *
 * <b>The offer is fetched only once she asks</b> (D-342). Most families have
 * nothing to repair, and a request per dancer on every Home load would buy a
 * list nobody opened. No window is passed, so the server answers today
 * onward - its own default (D-176).
 */
function useParentRepair(dancer: PortalDancerView): (night: MissedSession) => ReactNode {
  const [repairing, setRepairing] = useState<string | null>(null)

  /*
   * ⚠ **The two branches below are two capabilities** (D-920), and holding
   * them behind one is how a control that cannot work got offered.
   *
   * A night repaired by a private opens an `AskForm` that **posts** to a door
   * now requiring `BookPrivates`; every other night opens a makeup list, which
   * is free. Gated together on the free switch, a dancer granted only
   * `ArrangeAttendance` was shown the private form and answered **403** —
   * which is the exact failure the comment on `renderRepair` was written about
   * one commit earlier, re-made by the split that was meant to prevent it.
   */
  const mayBook = useCan('BookPrivates')

  const {
    data: offer,
    isError: offerFailed,
    isPending: offerPending,
  } = useQuery({
    queryKey: ['portal-makeup', dancer.dancerId],
    queryFn: () => fetchMakeupOffer(dancer.dancerId),
    enabled: repairing !== null,
  })

  return (night) =>
    night.repairedByPrivate ? (
      !mayBook ? null : (
      <AskForm
        dancerId={dancer.dancerId}
        dancerName={dancer.name}
        paysFromCredit={dancer.paysFromCredit}
        // This absence and no other: reached from one row, the row is the
        // answer (D-340).
        repairable={[{ occurrenceId: night.occurrenceId, className: night.className, date: night.date }]}
        presetRepairs={night.occurrenceId}
        trigger="inline"
      />
      )
    ) : (
      <RepairRow
        missed={night}
        dancerId={dancer.dancerId}
        open={repairing === night.occurrenceId}
        onOpen={() => setRepairing(night.occurrenceId)}
        offer={offer}
        pending={offerPending}
        failed={offerFailed}
      />
    )
}

/**
 * The parent's attendance panel: the shared one, with her controls in it (D-589).
 *
 * <b>The makeups live with the absences they repair</b> (D-348), where the
 * owner put them - and now under the class each one belongs to. The general
 * offer at the foot carries the half nothing else states: the entitlement
 * line, the unmarked-registers caveat (D-118), the away note (D-275).
 */
function Standing({ dancer }: { dancer: PortalDancerView }) {
  const { data: content } = useQuery({ queryKey: ['content'], queryFn: fetchContent })
  const renderRepair = useParentRepair(dancer)

  /*
   * <b>What she is already holding</b> (D-757, moved here by its amendment).
   *
   * <b>Here rather than inside the makeup offer, and that was the whole bug
   * the first cut shipped.</b> It went in beside *Bank a class for later*,
   * which reads sensibly and is behind <i>View makeup classes</i> - collapsed
   * until pressed. So the balance was visible only to a parent already hunting
   * for a makeup, when the balance is the thing that tells her whether she
   * needs to. `asked`: *"It still doesn't seem to show what is actually in the
   * bank for a dancer."*
   *
   * The panel's footer renders outside its own fold, so this is on screen the
   * moment she opens her dancer - no query gate either, because a query that
   * runs only when something is expanded was the same mistake one layer down.
   */
  const { data: banked } = useQuery({
    queryKey: ['portal-banked', dancer.dancerId],
    queryFn: () => fetchBankedFor(dancer.dancerId),
  })

  const mayAct = useCan('ArrangeAttendance')

  return (
    <AttendancePanel
      standing={dancer.standing}
      missed={dancer.missed}
      viewer="parent"
      /*
       * ⚠ **Every repair control hangs off this one prop** (D-918), and for one
       * commit it was passed unconditionally while the buttons *below* it were
       * gated — so a dancer got the two that matter most and the audit found
       * them on the screen her own report was about.
       *
       * The worse of the two was not the dead *Find a makeup* list: it was the
       * inline private-lesson form, which **submits**, because
       * `/api/portal/private-requests` resolves the household through a
       * Guardian link and answers *"We could not find that dancer"* — the app
       * telling a child it cannot find her, in reply to a control it offered
       * her. *A control whose only possible outcome is a refusal is worse than
       * no control*, which is a sentence this file already says twice.
       *
       * Decided here because this is where the capability is known. Gating the
       * three controls separately is how they came to disagree.
       */
      renderRepair={mayAct ? renderRepair : undefined}
      notOffered={text(content, 'portal.makeup.notOfferedForThisClass', 'Makeups are not offered for this class.')}
      footer={
        <>
          {/*
            Her banked classes are a *record* and stay (D-918).
          */}
          <BankedInHand banked={banked} />

          {/*
            The makeup offer is a list of classes to **book**, so for a reader
            who may not book it is a page of controls that do nothing. The
            makeups she already holds are rows on her schedule, not here, so
            nothing readable is lost by leaving it out.
          */}
          {mayAct && <MakeupOffer dancer={dancer} standing={dancer.standing} />}
        </>
      }
    />
  )
}


/**
 * What a family could do about the classes their dancer has missed (D-276).
 *
 * <b>Closed until asked.</b> It sits below the schedule on a phone and most
 * weeks has nothing to say, so it earns its space only when opened — the same
 * reasoning D-271 applied to the missed-dates list.
 *
 * <b>Every word here is Tonya's</b> (A-16, NFR-14). The label, the entitlement
 * sentence, the nothing-missed line, the unmarked caveat and the refusal are all
 * content blocks; the counts arrive as merge values because a number cannot live
 * in one.
 */
export function MakeupOffer({
  dancer,
  from,
  to,
  standing,
  embedded = false,
  onBooked,
  heading,
  nothingFits,
}: {
  dancer: PortalDancerView
  /** Omitted where there is no week on screen to inherit (D-348). */
  from?: string
  to?: string
  standing?: DancerStanding
  /**
   * Drop the toggle, for a caller that is already a disclosure.
   *
   * <b>Nothing passes this today</b> — it existed for the schedule drawer, which
   * is gone (D-348), and the offer now sits in the attendance panel where it
   * keeps its own toggle so the fetch stays lazy. Left in place because the
   * reasoning still holds for the next caller that wraps it, and deleting a
   * prop to add it back is churn; but it is a prop with one value, which is the
   * shape D-297 warned nobody can reason about.
   *
   * The original reason: a parent who has already opened one thing should not
   * have to open the thing inside it — two levels of disclosure is one too
   * many, and the second hides exactly what she came for.
   */
  embedded?: boolean
  /**
   * A makeup booked from here, with the teacher's question when one was
   * raised (D-630) - a conversation that opened this door pins it, and has
   * nothing to pin when the class was at her own level.
   */
  onBooked?: (askId: string | null) => void
  /**
   * The line over the list when embedded; null for a caller with a heading
   * of its own (the conversation's makeup door, D-630), so the dialog does
   * not carry two.
   */
  heading?: string | null
  /**
   * What to say when nothing in the window fits. The portal's line says
   * *try another week*, which a placement with no week control cannot
   * offer (D-630); the caller that has none says something it can.
   */
  nothingFits?: string
}) {
  const [open, setOpen] = useState(false)
  const { data: content } = useQuery({ queryKey: ['content'], queryFn: fetchContent })
  const queryClient = useQueryClient()

  /*
   * Booking, from the list that offered it (D-283).
   *
   * <b>Both ids.</b> A makeup credits the class that was missed rather than the
   * one she sat in, so the pair travels together - and the server re-checks it
   * against its own offer, because the number on this screen is a snapshot and
   * another device may have spent it.
   *
   * The schedule is invalidated alongside the offer: a booked session belongs on
   * it immediately (D-278), and a family who books something she cannot then see
   * has no way to know it worked.
   *
   * <b>On settle, not on success.</b> A refusal is exactly the case where this
   * screen is out of date - another device spent the seat, or the entitlement.
   * Showing the words while leaving the stale count and a live Book button
   * beside them invites the same tap and the same refusal indefinitely, because
   * nothing here refetches on focus. Scenario 42 asks for the true figure, and
   * the true figure is the half that stops a correct refusal reading as a bug.
   */
  const book = useMutation({
    mutationFn: ({ candidate, repairs }: { candidate: MakeupCandidate; repairs: string }) =>
      bookMakeup(dancer.dancerId, candidate.occurrenceId, repairs),
    onSuccess: (booked) => onBooked?.(booked.askId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-makeup', dancer.dancerId] })
      queryClient.invalidateQueries({ queryKey: ['portal-dancer', dancer.dancerId] })
    },
  })

  const {
    data: offer,
    isPending,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['portal-makeup', dancer.dancerId, from, to],
    queryFn: () => fetchMakeupOffer(dancer.dancerId, from, to),
    enabled: embedded || open,
  })

  /*
   * What she can bank for later (D-611). Its own query beside the offer rather
   * than part of it: the offer answers "what makes up what she missed", and a
   * dancer who has missed nothing correctly gets nothing from it.
   */
  const { data: bankable } = useQuery({
    queryKey: ['portal-bank', dancer.dancerId, from, to],
    queryFn: () => fetchBankableSessions(dancer.dancerId, from, to),
    enabled: embedded || open,
  })


  const bank = useMutation({
    mutationFn: (session: BankableSession) => bankSession(dancer.dancerId, session.occurrenceId),
    // Banking above her level asks the teacher too (D-611, D-403): the same
    // question, so the same card (D-630).
    onSuccess: (booked) => onBooked?.(booked.askId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-bank', dancer.dancerId] })
      /*
       * <b>Named in full, because a key is not a prefix of a string</b>
       * (D-757, found by the audit). React Query matches keys element-wise,
       * and `'portal-banked' !== 'portal-bank'` - so the balance above is not
       * invalidated by the line before this however much it looks like it is.
       * Harmless today, because banking takes a seat on a future session and a
       * credit needs a register (D-592); a trap for whoever makes it immediate.
       * D-210: checking your own work has to show your own work.
       */
      queryClient.invalidateQueries({ queryKey: ['portal-banked', dancer.dancerId] })
      queryClient.invalidateQueries({ queryKey: ['portal-makeup', dancer.dancerId] })
      queryClient.invalidateQueries({ queryKey: ['portal-dancer', dancer.dancerId] })
    },
  })

  const label = heading === undefined ? text(content, 'portal.makeup.label', 'View makeup classes') : heading

  const notYetMarked = standing?.overall?.notYetMarked ?? 0

  return (
    <div className="w-full max-w-full">
      {!embedded && (
        <div className="flex justify-end">
          <button
            type="button"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="rounded-lg border border-line-2 px-3 py-1.5 text-[12.5px] font-semibold text-ink-2 hover:border-ink-3"
          >
            {label}
          </button>
        </div>
      )}

      {(embedded || open) && (
        <div
          className={
            embedded
              ? 'w-full max-w-full'
              : 'mt-3 w-full max-w-full rounded-xl border border-line-2 p-3'
          }
        >
          {embedded && label !== null && (
            <p className="mb-2 text-[12px] font-semibold text-ink-3">
              {label}
            </p>
          )}
          {isPending && (
            <p className="text-[13px] text-ink-3">Checking...</p>
          )}

          {/*
            Never a statement about the data (D-209). The first ~75 seconds
            after a deploy are 503s, and "no makeups available" would be a
            confident claim about a range nothing was ever read from.
          */}
          {isError && (
            <div className="flex flex-col gap-2">
              <p className="text-[13px] text-ink-2">
                {text(
                  content,
                  'portal.makeup.unavailable',
                  'We could not check for makeup classes right now.',
                )}
              </p>
              <div>
                <button
                  type="button"
                  onClick={() => void refetch()}
                  className="rounded-lg border border-line-2 px-3 py-1.5 text-[12.5px] font-semibold text-ink-2 hover:border-ink-3"
                >
                  Try again
                </button>
              </div>
            </div>
          )}

          {offer && !isError && (
            <div className="flex flex-col gap-2">
              {/*
                **"Nothing missed" means nothing at all, including what has run
                out of time** (D-653).

                `missed` alone is the commonest company case and reads as a flat
                contradiction: one absence five weeks ago and nothing since
                printed *"Nothing missed this season."* directly above *"1 too
                late to make up"*, with the second one being the true half.
                Found by an audit; it had no rendering test, which is why.
              */}
              {offer.missed === 0 && (offer.tooLate ?? 0) === 0 ? (
                <p className="text-[13px] text-ink-2">
                  {text(
                    content,
                    'portal.makeup.nothingMissed',
                    'Nothing missed this season.',
                  )}
                </p>
              ) : (
                <p className="text-[13px] font-semibold text-ink">
                  {fill(
                    content,
                    'portal.makeup.entitlement',
                    {
                      /*
                        **Everything she missed, not everything she may still
                        book** (D-653). The wire splits the two — `missed` is
                        what is bookable and `tooLate` what is not — because
                        the booking path needs the first. The *sentence* says
                        "missed", and a dancer with one absence in time and one
                        out of it missed two; printing 1 would tell a parent her
                        daughter missed fewer classes than she did, which is the
                        one thing this number must never do.

                        `tooLate` is 0 for everybody but the company team, so
                        for almost every family this is unchanged.
                      */
                      missed: offer.missed + (offer.tooLate ?? 0),
                      booked: offer.booked,
                      available: offer.available,
                    },
                    '{{missed}} missed · {{booked}} booked · {{available}} you can still book',
                  )}
                </p>
              )}

              {/*
                **What has run out of time** (D-653). Only the company team has
                a deadline, so this is silent for everybody else.

                Its own line rather than a fourth term in the sentence above:
                that one is the arithmetic of what she may still do, and this
                is not part of it. Saying nothing would be worse than either —
                the absence would simply stop appearing, and *missed* would read
                as though she had missed fewer classes than she did.
              */}
              {(offer.tooLate ?? 0) > 0 && (
                <p className="mt-1 text-[12.5px] text-ink-3">
                  {fill(
                    content,
                    'portal.makeup.tooLate',
                    { tooLate: offer.tooLate ?? 0 },
                    '{{tooLate}} too late to make up. They still count toward attendance.',
                  )}
                </p>
              )}

              {/*
                A register left unmarked is excluded and reported as a gap
                (D-118), so the figure above is provisional whenever one is
                outstanding. Saying so beats a number that moves next week with
                no explanation.
              */}
              {notYetMarked > 0 && (
                <p className="text-[12.5px] text-ink-3">
                  {fill(
                    content,
                    'portal.makeup.unmarkedCaveat',
                    { count: notYetMarked },
                    'Some classes have not been marked yet, so this may change.',
                  )}
                </p>
              )}

              {offer.missed > 0 && offer.candidates.length === 0 && (
                <p className="text-[12.5px] text-ink-3">
                  {nothingFits ??
                    text(
                      content,
                      'portal.makeup.nothingThisWeek',
                      'Nothing in this week fits - try another week.',
                    )}
                </p>
              )}

              {/*
                An away day is out of the denominator entirely, so nothing is
                offered against it — and a parent who reported one and then finds
                no makeup for it is owed the reason rather than a silence. The
                teacher's door still offers it, deliberately (D-275).
              */}
              {(standing?.overall?.excused ?? 0) > 0 && (
                <p className="text-[12.5px] text-ink-3">
                  {text(
                    content,
                    'portal.makeup.awayNote',
                    'Days you told us about do not count against her, so there is nothing to make up.',
                  )}
                </p>
              )}

              {/*
                A refusal arrives as words, never as a closed dialog (D-273).
                The server re-checks the entitlement, so a stale screen can be
                told no — and it has to say what happened.
              */}
              {book.isError && (
                <p role="alert" className="text-[12.5px] text-crit">
                  {(book.error as Error).message}
                </p>
              )}

              <ul className="flex flex-col gap-2">
                {offer.candidates.map((candidate) => (
                  <MakeupCandidateCard
                    key={candidate.occurrenceId}
                    candidate={candidate}
                    content={content}
                    /*
                      The label. From this list she has not named an absence, so
                      the oldest one the session repairs is the default (D-276).
                    */
                    repairs={candidate.repairsOccurrenceId}
                    onBook={(c, repairs) => book.mutate({ candidate: c, repairs })}
                    booking={
                      book.isPending ? book.variables?.candidate.occurrenceId ?? null : null
                    }
                  />
                ))}
              </ul>
            </div>
          )}


          {/*
            Classes she can take now and spend later (D-611).

            `asked`: "i also thought we had it setup where you could signup for
            makeups even if you hadn't missed something to bank them for the
            future". Below the makeups, because repairing something she has
            already missed is the more urgent of the two -- but shown whether
            or not there is anything above it, which is the whole point.
          */}
          {bankable && bankable.length > 0 && (
            <div className="mt-4 flex flex-col gap-2 border-t border-line pt-3">
              <p className="text-[13px] font-semibold text-ink">
                {text(content, 'portal.bank.heading', 'Bank a class for later')}
              </p>
              <p className="text-[12.5px] text-ink-3">
                {text(
                  content,
                  'portal.bank.blurb',
                  'Take one of these now and it will cover a class she misses later.',
                )}
              </p>

              {bank.isError && (
                <p role="alert" className="text-[12.5px] text-crit">
                  {(bank.error as Error).message}
                </p>
              )}

              <ul className="flex flex-col gap-2">
                {bankable.map((session) => (
                  <BankableSessionCard
                    key={session.occurrenceId}
                    session={session}
                    content={content}
                    onBank={(s) => bank.mutate(s)}
                    banking={bank.isPending ? bank.variables?.occurrenceId ?? null : null}
                  />
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * A parent's whole season, a week at a time (D-176).
 *
 * **This replaces the studio master calendar for parents rather than filtering
 * it.** Scoping has to be structural: the master calendar's week is cached for
 * the whole studio and scoped afterwards (D-83), so a filtered version is one
 * bug away from showing a parent every child in the studio. This asks only for
 * her dancer and can never widen.
 *
 * It also fixes the gap that prompted all of this — the schedule could not move
 * off the current fortnight, so a parent could not answer "what does the week of
 * the recital look like".
 *
 * Weeks start Monday, matching how the studio runs and how attendance groups
 * count a week (D-170).
 */
/**
 * Which child's schedule you are looking at (D-176).
 *
 * One component because the Home tab and the schedule both draw it, and a
 * second copy is how the two News panels came to disagree.
 *
 * Multi-dancer by default, not as an afterthought — families with siblings must
 * not juggle logins (PRD §4). `flex-1` only while the row is the full width of a
 * phone: given a laptop it stops stretching, because four buttons spread across
 * 60rem read as a navigation bar rather than a choice between children.
 *
 * **Only where it changes something** (D-257). Account shows the whole
 * household and News is the studio's, so on those it rendered, highlighted a
 * name and did nothing — reported from the pilot as "clicking them doesn't do
 * anything". A switcher that moves a highlight and changes no content is a
 * control that lies with a coat of paint on it.
 */
export function DancerPicker({
  dancers,
  value,
  onChange,
  includeEveryone = true,
}: {
  dancers: PortalHome['dancers']
  value: string | null
  onChange: (id: string) => void
  /**
   * Offer the combined view as well as the individual ones (D-388).
   *
   * <b>False on Home, true on Schedule, and the difference is real.</b> On
   * Schedule, *Everyone* is a combined **week** — three children's classes laid
   * out together, which no per-dancer tab shows. On Home it rendered each
   * dancer's next class side by side, and the Today panel above it now says
   * exactly that, one row per person (D-380). Reported as *"the everyone can be
   * taken off for this page. it is redundent now"*, and she is right: it was
   * the same answer twice on one screen.
   *
   * A prop rather than two pickers, because the chips, the sizing and the
   * phone-width behaviour are the same control — and two copies of it would
   * drift, which is how this project keeps arriving here.
   */
  includeEveryone?: boolean
}) {
  // A household of one never sees it at all.
  if (dancers.length <= 1) return null

  const style = (active: boolean) =>
    `flex-1 rounded-xl border px-3 py-2.5 text-[13.5px] font-semibold transition-colors sm:flex-none sm:px-5 ${
      active
        ? 'border-accent bg-accent-soft text-accent-2'
        : 'border-line bg-surface text-ink-2 hover:border-ink-3'
    }`

  return (
    <div className="mb-4 flex flex-wrap gap-2">
      {dancers.map((d) => (
        <button
          key={d.dancerId}
          type="button"
          onClick={() => onChange(d.dancerId)}
          className={style(d.dancerId === value)}
        >
          {d.firstName}
        </button>
      ))}

      {/*
        One evening, all the children (D-176). The question a parent with three
        dancers actually asks on a Tuesday is "where does everyone need to be",
        and answering it by tapping between three schedules and holding the
        answer in her head is the thing the paper diary was already better at.

        Still true on Schedule, where this is a combined week. Not on Home any
        more — the Today panel answers it there (D-388).
      */}
      {includeEveryone && (
        <button type="button" onClick={() => onChange(ALL)} className={style(value === ALL)}>
          Everyone
        </button>
      )}
    </div>
  )
}

/**
 * A family's schedule, whole (D-322).
 *
 * **One composition, and now one caller.** It served the portal's Schedule tab
 * and `/schedule` at once while both existed; the tabs are gone (D-322), so
 * only the route renders it. Building the route out of the same pieces in a
 * different arrangement is what produced two News panels that had silently
 * drifted, which is why it was shared rather than copied.
 *
 * **Which week, which view and which child live in the URL** (D-329). A tab
 * kept them because it never unmounted; a route does not, so leaving the screen
 * and coming back landed on today — a regression against a decision D-181 had
 * already paid for once. Nothing above a route survives a route, so the honest
 * home for "which week am I looking at" is the address bar.
 *
 * **And it makes the week linkable**, which is the same want that made the tabs
 * addressable in the first place: a parent sending Tonya *"look at this week"*
 * could not, and now can.
 *
 * The state still sits *above the schedule* either way: switching dancer
 * unmounts one and mounts another, and state owned down there dies with it
 * (D-181). The address is simply higher than any of it.
 */
export function ParentSchedulePanel({ home: given }: { home?: PortalHome } = {}) {
  const [params, setParams] = useSearchParams()

  /*
   * Replaced rather than pushed, so paging four weeks forward does not leave
   * four entries in the back stack. Back should leave the screen, not undo one
   * click of a stepper.
   */

  /**
   * <b>One write, because two cannot compose</b> (D-435).
   *
   * Reported: tapping 12 September in the month view opened the day view on
   * <b>1 September</b> — the month's own anchor — <i>"but after doing the same
   * thing multiple times it will sometimes work properly, very odd"</i>.
   *
   * Opening a day changes two things, and it did so with two calls. Each built
   * a fresh `URLSearchParams` from the `params` of the current render, so the
   * second wrote the week straight back to what it had been.
   *
   * <b>The functional form does not fix it, which is worth writing down because
   * it is the obvious thing to reach for.</b> `setSearchParams` is
   * `navigate()` underneath, not a React state setter: two calls in one tick
   * are two navigations, and the second still reads the location from before
   * the first. Tried, and it failed twelve times out of twelve in a browser.
   *
   * So anything changing more than one key says so in a single call.
   */
  const set = (changes: Record<string, string>) => {
    const next = new URLSearchParams(params)
    for (const [key, value] of Object.entries(changes)) next.set(key, value)
    setParams(next, { replace: true })
  }

  /*
   * The day the dialog should open on, or null when it is closed (D-177).
   *
   * A *date* rather than a boolean, which is what lets the schedule say which
   * day it is showing — the button lives beside the anchor rather than the
   * anchor being drilled up here.
   */
  const [reporting, setReporting] = useState<string | null>(null)

  // Only when nothing handed it over, so the portal does not fetch twice.
  const {
    data: fetched,
    isPending,
    isError,
  } = useQuery({
    queryKey: ['portal'],
    queryFn: fetchPortalHome,
    enabled: given === undefined,
  })

  const home = given ?? fetched

  const dancerId = params.get('dancer')
  const setDancerId = (id: string) => set({ dancer: id })

  /*
   * Read straight from the address, with the same defaults a first visit always
   * had: this week, as a week. An unrecognised value falls back rather than
   * rendering nothing — a hand-edited URL is somebody exploring, not an error
   * to shout about.
   */
  const asked = params.get('view')
  const view: ScheduleView = isScheduleView(asked) ? asked : 'week'
  const anchor = params.get('week') ?? today()

  const setView = (next: ScheduleView) => set({ view: next })
  const setAnchor = (date: string) => set({ week: date })

  /** The two together, which is the whole of D-435. */
  const openDay = (date: string) => set({ week: date, view: 'day' })

  /*
   * The full record, not the summary `home` carries - `ScheduleTab` needs the
   * sessions, the standing and the missed dates.
   *
   * Same query key as the portal's, so when both are mounted react-query serves
   * one request rather than two. Skipped for Everyone, which asks per dancer
   * further down.
   */
  const { data: dancerView } = useQuery({
    queryKey: ['portal-dancer', dancerId],
    queryFn: () => fetchPortalDancer(dancerId!),
    enabled: dancerId !== null && dancerId !== ALL,
  })

  /*
   * Whoever comes first, until a parent picks — written to the address so a
   * shared link opens on the same child rather than on whoever happens to sort
   * first for the person opening it.
   */
  useEffect(() => {
    if (!dancerId && home?.dancers.length) setDancerId(home.dancers[0].dancerId)
    // `set` is rebuilt every render; depending on it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [home, dancerId])

  /*
   * Three states, not one - and rendering none of them is what the audit found.
   *
   * The portal has all three above this component and the route had none, so on
   * the route a failed /api/portal drew a heading over nothing. **That is the
   * normal case rather than the rare one**: every database-backed endpoint
   * refuses for about 75 seconds after a deploy, and `retry: 1` gives up after
   * a second. Silence there reads as "your children have nothing on", which is
   * a missing lookup rendering as a statement about the data (D-209).
   *
   * Only when this component did the fetching; when the portal handed `home`
   * over it has already said all three.
   */
  if (given === undefined) {
    if (isPending) {
      return <p className="py-8 text-center text-[14px] text-ink-3">Loading...</p>
    }

    if (isError || !home) {
      return (
        <p className="py-8 text-center text-[14px] text-crit">
          Could not load your schedule. Please try again.
        </p>
      )
    }
  }

  if (!home) return null

  if (home.dancers.length === 0) {
    return (
      <p className="py-8 text-center text-[14px] text-ink-3">
        No dancers are linked to your account yet. Ask the studio if that looks wrong.
      </p>
    )
  }

  return (
    <>
      {/*
        Always drawn here, controlled or not - the caller draws it for its own
        other screens, never for this one. Gated on being uncontrolled it
        vanished from the portal's Schedule tab, which an existing test caught:
        the row is the answer to "whose evening is this?" and the schedule is
        the screen that asks it.
      */}
      <DancerPicker dancers={home.dancers} value={dancerId} onChange={setDancerId} />

      {dancerId === ALL ? (
        <EveryoneSchedule
          dancers={home.dancers}
          view={view}
          anchor={anchor}
          onView={setView}
          onAnchor={setAnchor}
          onOpenDay={openDay}
          onReport={setReporting}
        />
      ) : (
        dancerView && (
          <ScheduleTab
            dancer={dancerView}
            view={view}
            anchor={anchor}
            onView={setView}
            onAnchor={setAnchor}
            onOpenDay={openDay}
            onReport={setReporting}
          />
        )
      )}

      {reporting !== null && (
        <ReportAbsence
          dancers={home.dancers}
          // Whoever the schedule is showing, so the common case is already
          // answered — and Everyone means everyone.
          preselected={
            dancerId && dancerId !== ALL ? [dancerId] : home.dancers.map((d) => d.dancerId)
          }
          startOn={reporting}
          onClose={() => setReporting(null)}
        />
      )}
    </>
  )
}

function ScheduleTab({
  dancer,
  view,
  anchor,
  onView,
  onAnchor,
  onOpenDay,
  onReport,
}: {
  dancer: PortalDancerView
  view: ScheduleView
  anchor: string
  onView: (v: ScheduleView) => void
  onAnchor: (date: string) => void
  /** Opening a day changes the anchor and the view together (D-435). */
  onOpenDay: (date: string) => void
  onReport: (date: string) => void
}) {

  const { from, to, label } = scheduleWindow(view, anchor)

  const { data: window, isPending } = useQuery({
    queryKey: ['portal-dancer', dancer.dancerId, from, to],
    queryFn: () => fetchPortalDancer(dancer.dancerId, from, to),
  })

  const days = byDate(window?.upcoming ?? [])

  return (
    <div className="flex flex-col gap-4">
      {/*
        Week leads because it is the question parents actually ask. Day and
        month exist for planning around a recital or a holiday, which is the
        reason the studio calendar was worth folding in at all (D-176).
      */}
      {/*
        One control instead of three, above the view toggle (D-297).

        Reported from live testing: three right-aligned buttons stacked down the
        page read as clutter on a laptop and worse on a phone. They are all
        about this schedule, so they belong inside it — and the order now says
        *who → what you are doing → how you are looking*.

        <b>Makeups and privates are one thing to a parent.</b> She knows her
        dancer missed a class; she does not know whether the repair is a drop-in
        (D-274) or a named private (D-286) — that is our model, and making her
        choose between two panels pushed it onto her.
      */}
      {/*
        The same header the teaching half has, from the same component (D-359).

        **The owner asked why these two differed and the honest answer was that
        nobody had compared them.** This half had a segmented toggle, plain
        arrows and no way to pick a date; the other had a picker on its title
        and a dropdown. One tap apart, inside one destination.

        The date picker is the part this half was missing outright: with arrows
        alone, a parent planning around a recital in November taps eight times.
      */}
      <ScheduleHeader
        label={label}
        anchor={anchor}
        view={view}
        onPick={onAnchor}
        onView={(next) => isScheduleView(next) && onView(next)}
        onStep={(d) => onAnchor(stepAnchor(view, anchor, d))}
        onToday={() => onAnchor(today())}
        hideTeachers
        actions={
          <ScheduleControls
            view={view}
            onReport={onReport}
            anchor={anchor}
            dancer={{
              id: dancer.dancerId,
              name: dancer.name,
              paysFromCredit: dancer.paysFromCredit,
            }}
            /*
              The missed solos and duets, which is what an ask can repair.
              Supplied by the caller rather than fetched again, because the
              schedule already knows what she missed and a second opinion can
              disagree (D-275).
            */
            repairable={repairableOf(window?.missed ?? [])}
          />
        }
      />

      {isPending && <p className="py-6 text-center text-[13px] text-ink-3">Loading...</p>}

      {view === 'month' && !isPending && (
        <MonthGrid
          anchor={anchor}
          sessions={window?.upcoming ?? []}
          onPick={onOpenDay}
        />
      )}

      {view !== 'month' && !isPending && days.length === 0 && (
        <p className="rounded-2xl border border-line bg-surface px-5 py-8 text-center text-[14px] text-ink-3">
          {/*
            Genuinely empty, and why depends on where in the season it falls —
            before it starts, that is a date rather than an absence (D-176). The
            navigation stays above so she can move on.
          */}
          {window?.startsOn
            ? `Classes start ${formatFullDate(window.startsOn)}.`
            : `Nothing this ${view}.`}
        </p>
      )}

      {/*
        Two columns of days once there is room. A busy September week is seven
        stacked cards on a laptop otherwise, most of the screen empty beside
        them - the complaint that started this.
      */}
      <div className={view === 'week' ? 'grid gap-4 lg:grid-cols-2 lg:items-start' : 'flex flex-col gap-4'}>
      {view !== 'month' &&
        days.map((day) => (
          <section key={day.date}>
            <h2 className="mb-1.5 text-[11px] font-bold tracking-[0.11em] text-ink-3 uppercase">
              {day.date === today() ? 'Today' : formatFullDate(day.date)}
            </h2>

            <div className="rounded-2xl border border-line bg-surface px-4">
              {day.sessions.map((session) => (
                <SessionRow
                  key={session.occurrenceId}
                  session={session}
                  dancerId={dancer.dancerId}
                />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  )
}

/**
 * A month at a glance, for planning around a recital or a holiday.
 *
 * Deliberately not a miniature timetable: at phone width a cell fits a count,
 * not a class name — and `COMP_TECH_BALLET_1PRETEAM` would not fit at any width.
 * Tapping a day opens it, which is where the detail lives.
 */
function MonthGrid({
  anchor,
  sessions,
  onPick,
  owners,
}: {
  anchor: string
  sessions: PortalSession[]
  onPick: (date: string) => void
  /**
   * Whose class each session is, in the whole-household view (D-176).
   *
   * Absent for a single dancer, where the question is only "how busy is that
   * evening" — the answer to "whose" is already the chip at the top.
   */
  owners?: Map<string, string>
}) {
  const { year, month } = monthOf(anchor)

  /*
   * Grouped by day, then by dancer where there is more than one.
   *
   * **The dots carry the room colour**, the same language as the week view and
   * the master calendar — the studio colour-codes its rooms in the real world
   * and one screen disagreeing about a colour is worse than none of them having
   * it.
   *
   * The *initial* carries whose class it is, because at month scale a parent is
   * planning rather than arriving: "is the 14th busy, and which child" changes
   * the plan, while which door to walk through does not.
   */
  const byDay = new Map<string, Map<string, PortalSession[]>>()

  for (const s of sessions) {
    const day = byDay.get(s.date) ?? new Map<string, PortalSession[]>()
    const who = owners?.get(s.occurrenceId) ?? ''

    day.set(who, [...(day.get(who) ?? []), s])
    byDay.set(s.date, day)
  }

  return (
    <div>
      <div className="mb-1 grid grid-cols-7 gap-1 text-center text-[10.5px] font-bold text-ink-3">
        {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
          <span key={i}>{d}</span>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {monthGrid(year, month).map((date) => {
          const day = byDay.get(date)
          const count = day ? [...day.values()].flat().length : 0
          const outside = monthOf(date).month !== month

          return (
            <button
              key={date}
              type="button"
              onClick={() => onPick(date)}
              className={`min-h-[3.25rem] rounded-lg border p-1 text-[12px] transition-colors ${
                date === today()
                  ? 'border-accent bg-accent-soft font-bold text-accent-2'
                  : count > 0
                    ? 'border-line bg-surface font-semibold hover:border-ink-3'
                    : 'border-transparent bg-surface-2 text-ink-3 hover:border-line'
              } ${outside ? 'opacity-40' : ''}`}
            >
              <span className="tabular block leading-none">{Number(date.slice(8, 10))}</span>

              {day && (
                <span className="mt-1 flex flex-col items-center gap-0.5">
                  {[...day.entries()].slice(0, 3).map(([who, theirs]) => (
                    <span key={who} className="flex items-center gap-0.5 leading-none">
                      {/* Only where several children are merged; otherwise the
                          chip at the top already answers whose. */}
                      {who && (
                        <span className="text-[9px] font-bold text-ink-3">{who.slice(0, 1)}</span>
                      )}

                      {theirs.slice(0, 3).map((session) => (
                        <span
                          key={session.occurrenceId}
                          className="room-tint size-1 rounded-full"
                          style={
                            {
                              '--room-color': session.roomColor ?? 'var(--color-accent)',
                              background: 'var(--room-display)',
                            } as React.CSSProperties
                          }
                        />
                      ))}
                    </span>
                  ))}
                </span>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}

/**
 * Everything a parent can *do* about this schedule, behind one control (D-297).
 *
 * **Three surfaces became one.** Reporting an absence, the makeup offer and
 * private lessons each had their own right-aligned button stacked down the
 * page — clutter on a laptop and worse on a phone, reported from live testing.
 * They are all about the schedule below them, so they live in it.
 *
 * **Deliberately not a global nav item.** Phone space is the constraint, and
 * the communication phase will want a slot of its own (M3); spending one now
 * would be borrowing against it.
 *
 * **Makeups and privates are one thing to a parent.** She knows her dancer
 * missed a class. Whether the repair is a drop-in that already runs (D-274) or
 * a named private for a solo (D-286) is our distinction, not hers — so both are
 * inside, and the panels render without a second layer of disclosure.
 */
/**
 * A private lesson time waiting on this family, wherever they are (D-297).
 *
 * **Derived, never marked resolved** (D-285): the moment they accept or decline
 * it, the next read has nothing to show and the banner goes. Nothing anywhere
 * has to be told.
 *
 * **It names the dancer.** A household with four children gets "Ellie's private
 * lesson time" rather than a count they then have to go and attribute — and
 * tapping it lands on the schedule, where the answer lives.
 */
/**
 * A proposed time, answered where she is told about it (D-344).
 *
 * <b>It used to be a banner that navigated.</b> D-297 moved it out of the
 * privates panel a week ago so a proposed time was visible without scrolling -
 * and the destination it moved to, `/schedule`, hid it behind a collapsed
 * accordion. The owner then found the other half: a notification about one
 * child opened a *different* child, because `hrefForSubject` sends a parent to
 * a bare `/schedule` and that opens the first dancer alphabetically.
 *
 * <b>The fix is not a better link, it is not needing one.</b> She accepts or
 * declines here. D-286 already limits her to exactly those two - there is no
 * counter-proposal while there is no way to talk in the app - so the whole
 * decision fits on the card.
 *
 * <b>Household-scoped, never dancer-scoped.</b> The picker must not be able to
 * hide a question somebody is waiting on, and scoping it per dancer would
 * recreate the owner's bug from the inside.
 */
function SessionRow({
  session,
  who,
  dancerId,
}: {
  session: PortalSession
  /** Whose class, shown only where several dancers are merged (D-176). */
  who?: string
  /**
   * Whose row it is, so a booked makeup can be given back (D-283). Absent only
   * where the caller genuinely does not know - and then the row still says what
   * it is for, it just cannot offer the control.
   */
  dancerId?: string
}) {
  const { data: content } = useQuery({ queryKey: ['content'], queryFn: fetchContent })
  const queryClient = useQueryClient()

  /*
   * **Both controls on this row give something back** — a makeup seat, a
   * private — so both are acts rather than readings (D-918). A dancer's own
   * login reads her week and cancels nothing on it until a parent grants it.
   */
  const mayAct = useCan('ArrangeAttendance')

  /*
   * Giving the seat back, from the row that holds it (D-283).
   *
   * A one-off needs three families to run, so a seat held by someone who is not
   * coming is the difference between the class happening and not - which makes
   * cancelling a property of the *class*, not a courtesy to the parent. Booking
   * without it also leaves an entitlement spent on a session she cannot attend,
   * and the only way out of that is ringing the studio.
   *
   * Settled rather than succeeded, for the same reason booking is (scenario 42).
   */
  const cancel = useMutation({
    mutationFn: () => cancelMakeup(dancerId!, session.occurrenceId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-makeup', dancerId] })
      queryClient.invalidateQueries({ queryKey: ['portal-dancer', dancerId] })
    },
  })

  /*
   * <b>Calling off a private lesson</b> (D-438, D-493).
   *
   * D-438 shipped the route and eleven tests and <b>nothing ever called
   * it</b> — a whole slice of a spec missing with no red run anywhere,
   * because endpoint tests pass perfectly against a screen that does not
   * exist. The owner agreed to a screen at that spec's own gate: <i>"inside
   * that the button is gone and the screen says to speak to the teacher."</i>
   *
   * <b>A booking, not an occurrence.</b> A private carries its booking id in
   * `occurrenceId` — the payload calls the field that because a class row
   * uses it for one, and the comment on `PortalSession` says a booking
   * belongs to no class.
   */
  /*
   * Which cancellation is waiting on a second tap (D-494). One value rather
   * than two booleans, so the two can never both be open.
   */
  const [confirming, setConfirming] = useState<'makeup' | 'private' | null>(null)

  const callOff = useMutation({
    mutationFn: () => cancelPrivate(session.occurrenceId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-dancer', dancerId] })
      queryClient.invalidateQueries({ queryKey: ['portal'] })
    },
  })

  return (
    /*
      The same room colour the studio already uses on the master calendar
      (D-176). The rooms are colour-coded in the real world and their names
      carry it — `1-Purple Room`, `4-Blue Room` — so a parent works out where to
      drop her dancer by colour long before she reads the room name. Two screens
      colouring the same room differently would be worse than neither.
    */
    <div
      className="room-tint border-b border-line py-3 last:border-b-0"
      style={
        {
          '--room-color': session.roomColor ?? 'var(--color-ink-3)',
        } as React.CSSProperties
      }
    >
      <div className="flex flex-wrap items-start gap-2">
        {/*
          The same 3px rule and the same `--room-display` the master calendar
          uses (CalendarWeekView), so a room reads identically wherever it
          appears. The studio colour-codes its rooms in the real world and the
          names carry it — `3-Pink Room`, `5-Red Room` — so two screens
          disagreeing about a colour is worse than neither having one.

          Where the master calendar tints the *time*, this tints the class name:
          a teacher scans a grid by time slot, a parent scans a list by which
          class it is.
        */}
        <div
          className="min-w-0 flex-1 border-l-[3px] pl-2.5"
          style={{ borderColor: 'var(--room-display)' }}
        >
          {who && (
            <p className="text-[11px] font-bold tracking-[0.08em] text-accent uppercase">{who}</p>
          )}
          {/*
            <b>The same name the calendar says</b> (D-579).

            This printed the raw Jackrabbit string while the week grid one file
            over now reads it, and a parent seeing
            <code>COMP_LYR | TU 400 | 9/10 | SMALL | COMP | MADDIE</code> where
            the studio says <i>Lyrical</i> is two implementations of one idea
            drifting in public. The comment a few lines up already says this
            card copies <code>CalendarWeekView</code>'s room colours "so a room
            reads identically wherever it appears"; the name is the same
            argument and was the half nobody had joined up.

            <b>The dancer is not repeated here.</b> The card already sits under
            her name in her own portal, so <i>Solo &middot; Ellie</i> on Ellie's
            schedule says it twice - unlike the master calendar, where the whole
            studio is on one grid and the dancer is what tells two solos apart.
          */}
          <p
            className="text-[14px] font-semibold wrap-anywhere"
            style={{ color: 'var(--room-display)' }}
            title={session.className}
          >
            {describeClass(session.className).label}
          </p>

          <p className="mt-0.5 text-[12.5px] text-ink-3">
            {session.date === today() ? 'Today' : formatFullDate(session.date)}
            {session.startTime && (
              <>
                {' '}
                &middot;{' '}
                <span className="tabular">
                  {clockTime(session.startTime)}
                  {meridiem(session.startTime)}
                </span>
              </>
            )}
            {session.roomName && <> &middot; {session.roomName}</>}
          </p>
        </div>

        {session.isCancelled ? (
          <span className="rounded-md bg-surface-2 px-2 py-0.5 text-[11.5px] font-semibold text-ink-3">
            Cancelled
          </span>
        ) : session.state === 'Excused' ? (
          <span className="rounded-md bg-surface-2 px-2 py-0.5 text-[11.5px] font-semibold text-ink-2">
            Away
          </span>
        ) : session.state === 'Unexcused' ? (
          /*
            An absence with no notice, said out loud (D-271).

            Only Excused and Cancelled carried a badge, so a dancer marked
            absent looked exactly like a session nobody had marked yet — and
            that is the one state a parent most needs to see. It fed the
            percentage, so it was never hidden; it just could not be traced to a
            day from this screen.

            <b>Present and Late stay silent on purpose.</b> A badge on every row
            is the noise D-177 stripped out of this list, and "she turned up"
            is not news.
          */
          <span className="rounded-md bg-warn-bg px-2 py-0.5 text-[11.5px] font-semibold text-warn">
            Absent
          </span>
        ) : session.state === 'Watched' ? (
          /*
            She was there and did not dance (D-634).

            Its own word rather than the absence badge: it counts against her
            in exactly the same way, but a parent whose daughter sat out with
            a sprained ankle would read "Absent" as the studio not having
            noticed she came.
          */
          <span className="rounded-md bg-warn-bg px-2 py-0.5 text-[11.5px] font-semibold text-warn">
            Watched
          </span>
        ) : session.state === 'NotExpected' ? (
          /*
            She was never called for this rehearsal (D-851, D-855).

            ⚠ <b>Without it this row is silent, and silence here reads as the
            session not having been marked yet</b> — which is the exact failure
            D-271 fixed for an absence, one state later. A parent of a Mini
            dancer sees the production rehearsal on the schedule every week and
            is not needed at all of them, so this is the row she is most likely
            to be looking at.

            <b>Not the absence colour</b>, because it is not one: it is out of
            the percentage entirely, and a warning tone on a night nobody asked
            her to would invite a phone call about a number that never moved.
          */
          <span className="rounded-md bg-surface-2 px-2 py-0.5 text-[11.5px] font-semibold text-ink-3">
            Not called
          </span>
        ) : null}
      </div>

      {/*
        A booked makeup is not one of her classes (D-283).

        It appears on this schedule because she has to turn up to it (D-278),
        and without saying so it reads as an ordinary session in a class her
        parent has never heard of — the same confusion the missed list had
        before it gained a role filter.

        <b>And a one-off has three answers, not two.</b> Provisional is the one
        a parent acts on; <i>did not run</i> is the one a boolean could not say.
        This schedule steps backwards, and a past session that never filled has
        already had its entitlement handed back (D-279) - so a row still reading
        "may not run yet" would contradict the offer panel above it, which at
        that moment is offering the same absence again. One derivation, on the
        server, read here (D-283).
      */}
      {/*
        <b>Only when the server says it is hers</b> (D-493).

        Four refusals decide it and two of them a browser cannot answer: a duet
        carries two households and neither may end the other's paid lesson, and
        a rehearsal is labelled <i>"Private with …"</i> like everything else.
        So the flag is asked for rather than worked out here — a control that
        refuses the person holding it is worse than no control.

        <b>Inside a day it is simply gone</b>, which is the gate's own answer:
        the bound is a nudge toward a conversation, and the endpoint's refusal
        names the teacher if the page has been open long enough to go stale.
      */}
      {/*
        ⚠ **Not gated on `mayAct`, and that is deliberate** (D-438). A dancer
        old enough to hold a login may call off her own private — asked for
        explicitly, and D-918 does not take it back. `mayCancel` is the
        server's answer and already carries the real bound.
      */}
      {session.mayCancel && (
        <p className="mt-1.5 pl-2.5 text-[12px] text-ink-3">
          <button
            type="button"
            onClick={() => setConfirming('private')}
            disabled={callOff.isPending}
            className="min-h-[44px] font-semibold text-accent underline underline-offset-2 disabled:opacity-50"
          >
            {/*
              <b>"Cancel", not "call off"</b> (D-504). `asked`, annotated on a
              screenshot: *"have it say cancel private, not call off"*. The
              studio says cancel, and the screen beside this one already did —
              a makeup is cancelled, a private was called off, for no reason
              anybody chose.
            */}
            {callOff.isPending
              ? text(content, 'portal.private.cancelling', 'Cancelling...')
              : text(content, 'portal.private.cancel', 'Cancel this private')}
          </button>

          {callOff.isError && (
            <span role="alert" className="mt-1 block font-semibold text-warn">
              {(callOff.error as Error).message}
            </span>
          )}
        </p>
      )}

      {/*
        <b>A second tap before anything is called off</b> (D-494).

        Both cancels on this row fired on one tap, and both send a real message
        to the other side — so a mistap is not a lost lesson, it is a teacher
        told her session is off. The dialog names the session and the day,
        because <i>"Are you sure?"</i> gives somebody who mistapped nothing to
        recognise and they confirm it anyway.
      */}
      {confirming && (
        <ConfirmAction
          title={
            confirming === 'private'
              ? text(content, 'portal.private.cancelTitle', 'Cancel this private?')
              : text(content, 'portal.makeup.cancelTitle', 'Cancel this makeup?')
          }
          /*
            <b>The label already names the teacher</b> (D-504). A private's
            `className` is built server-side as "Private with Victoria Hogan",
            so appending the instructor produced *"Private with Victoria Hogan
            on Wednesday, September 9, 2026 with Victoria Hogan"* — her name
            twice in one sentence, in the dialog whose whole job is to be
            recognised by somebody who mistapped. Reported in the same
            screenshot as the wording, unannotated.

            <b>And it no longer says "her"</b> (D-432). *"Her teacher will be
            told"* is about the dancer, and there are boys on the teams.
          */
          body={`${session.className} on ${
            session.date === today() ? 'today' : formatFullDate(session.date)
          }${
            !session.isPrivate && session.instructors.length > 0
              ? ` with ${session.instructors[0]}`
              : ''
          }. ${
            confirming === 'private'
              ? text(content, 'portal.private.cancelNote', 'The teacher will be told.')
              : text(
                  content,
                  'portal.makeup.cancelNote',
                  'The place will go back to whoever wants it.',
                )
          }`}
          confirmLabel={
            confirming === 'private'
              ? text(content, 'portal.private.cancelConfirm', 'Yes, cancel it')
              : text(content, 'portal.makeup.cancelConfirm', 'Yes, cancel it')
          }
          busyLabel="Cancelling..."
          busy={confirming === 'private' ? callOff.isPending : cancel.isPending}
          onDismiss={() => setConfirming(null)}
          onConfirm={() => {
            if (confirming === 'private') callOff.mutate()
            else cancel.mutate()

            setConfirming(null)
          }}
        />
      )}

      {/*
        <b>The practice links, when the teacher has added any</b> (D-541,
        D-596): the music first, then the videos.

        An unlisted YouTube link on the class, for a family helping at home.
        A new tab rather than an embed: the portal is not a video player, and
        YouTube's own page is where the family already knows how to scrub,
        slow down and loop. Absent on a private booking, which belongs to no
        class.
      */}
      <PracticeLinks
        classId={session.studioClassId}
        musicUrl={session.musicUrl}
        choreoLinks={session.choreoLinks}
        content={content}
      />

      {/*
        <b>A private can be waiting on a yes as well</b> (D-509).

        The server set `awaitingTeacher` on a private repairing a routine and
        nothing rendered it, because the only reader of that flag sat inside
        `session.isMakeup` — and a private row is built `isMakeup: false`. So
        the room was held, the lesson was unsettled, and the family's row read
        exactly like a settled one: the single thing D-403's own contract says
        this marker exists to prevent. The endpoint test that was supposed to
        guard it asserted the <i>JSON field</i>, which is why it was green
        while the screen showed nothing. Found by a cold audit.

        <b>Its own sentence, not D-403's.</b> That one says *her teacher*,
        meaning the dancer's own — true for a level-up, wrong here twice over:
        the person being waited on is the teacher of the missed routine, and
        there are boys on the teams (D-432). No pronoun, and it names whose
        say-so is outstanding.

        <b>Shown rather than hidden.</b> The evening is genuinely spoken for,
        so leaving it off her schedule would have her plan something on top of
        it. What it must not do is read as settled.
      */}
      {!session.isMakeup && session.awaitingTeacher && (
        <div className="mt-1 text-[11.5px] text-ink-3">
          {text(
            content,
            'portal.private.awaitingTeacher',
            'Waiting on the routine’s teacher to agree',
          )}
        </div>
      )}

      {/*
        <b>Where to pay her, on the row she comes back to</b> (D-691, item 36).

        `asked`: *"I also don't see their venmo information anywhere."* It was
        in exactly one place — the message raised when the lesson was agreed
        (D-666) — and a message is a moment. This is the screen that answers the
        same question next week.

        <b>Three sentences, and the server said which.</b> The branch is
        `PrivatePayment`, shared with the message sent at booking, so a family
        holding credit reads the same thing here as in her inbox. Choosing it
        here from a link and a flag is exactly what got it wrong first time:
        *paid directly to your teacher* is the sentence D-662 refuses for a
        dancer on credit.

        `paymentRoute` absent means nobody is billed — a rehearsal, a room a
        teacher holds for herself, or a lesson still waiting on her yes. It
        never means *we do not know*, which is `ToTeacherUnknownHow` and has a
        sentence of its own: a row that says nothing lets a parent assume the
        studio takes the money (D-209).
      */}
      {/*
        <b>What a private lesson makes up</b> (D-742).

        Her schedule draws a private once, as its booking, so this row is the
        only place the class it repairs can be named - and it is the row's own
        line rather than part of its name, because a name is put through
        `describeClass` and a Jackrabbit class name inside a sentence is parsed
        as the whole name (found by the audit).

        <b>Only where it repairs a named session.</b> A banked private carries
        no class here at all, because it has made nothing up yet (D-611) - the
        same line `isMakeup` draws below, and the same words, since it is the
        same sentence to a parent.
      */}
      {session.isPrivate && session.repairsClassName && (
        <div className="mt-1 text-[11.5px] text-ink-3">
          <span className="wrap-anywhere">
            {fill(
              content,
              'portal.makeup.repairs',
              { className: session.repairsClassName },
              'Making up {{className}}',
            )}
          </span>
        </div>
      )}

      {/*
        The sentences live in `PaymentLine` since D-705, because the
        confirmation a family answers before agreeing to a lesson a teacher
        arranged says the same thing, and two copies of the words that decide
        whether she pays somebody would drift.
      */}
      <PaymentLine
        route={session.paymentRoute}
        links={session.teacherPaymentLinks}
        className="mt-1 text-[11.5px] text-ink-3"
      />

      {session.isMakeup && (
        <div className="mt-1 text-[11.5px] text-ink-3">
          <span className="wrap-anywhere">
            {/*
              A banked class is not a makeup (D-611). It arrived here reading
              "Making up a missed class" to a parent who had missed nothing,
              beside a panel telling her so. D-610 drew this line for the night
              list; this row was left behind.
            */}
            {session.isBanked
              ? text(content, 'portal.bank.onSchedule', 'Banked for a class she misses later')
              : session.repairsClassName
                ? fill(
                    content,
                    'portal.makeup.repairs',
                    { className: session.repairsClassName },
                    'Making up {{className}}',
                  )
                : text(content, 'portal.makeup.repairsUnknown', 'Making up a missed class')}
          </span>

          {/*
            **Waiting on a person, not on a count** (D-403), which is why it is
            its own marker rather than a fourth `oneOffStatus`. A parent who
            reads *may not run yet* looks for more signups; this one is waiting
            on a yes, and there is nothing she can do to hurry it.
          */}
          {session.awaitingTeacher && (
            <span>
              {' '}
              &middot;{' '}
              {text(content, 'portal.makeup.awaitingTeacher', 'waiting on her teacher')}
            </span>
          )}

          {session.oneOffStatus === 'Provisional' && (
            <span> &middot; {text(content, 'portal.makeup.provisional', 'may not run yet')}</span>
          )}
          {session.oneOffStatus === 'DidNotRun' && (
            <span> &middot; {text(content, 'portal.makeup.didNotRun', 'did not run')}</span>
          )}

          {/*
            Cancelling is offered while it is still ahead of her. The server
            refuses a past one anyway (D-81), and a control whose only possible
            outcome is a refusal is worse than no control.
          */}
          {dancerId && mayAct && session.date >= today() && (
            <>
              {' '}
              &middot;{' '}
              <button
                type="button"
                onClick={() => setConfirming('makeup')}
                disabled={cancel.isPending}
                className="font-semibold text-accent underline underline-offset-2 disabled:opacity-50"
              >
                {cancel.isPending
                  ? text(content, 'portal.makeup.cancelling', 'Cancelling...')
                  : text(content, 'portal.makeup.cancel', 'Cancel')}
              </button>
            </>
          )}

          {cancel.isError && (
            <p role="alert" className="mt-1 text-[11.5px] font-semibold text-warn">
              {(cancel.error as Error).message}
            </p>
          )}
        </div>
      )}

      {/*
        No per-row "Can't make it?" any more (D-177).

        It was one link per session, so a busy Tuesday carried the same offer
        three times and a fortnight carried it a dozen — the clutter that
        prompted the rework, repeating a single action until it read as noise.
        Reporting is one control on the schedule, and it opens defaulted to the
        day on screen, so a single class is still a short path.
      */}
    </div>
  )
}

/**
 * What the studio has on file.
 *
 * **Read-only, with a way to flag a mistake** (D-123). A parent editing their
 * own address sounds helpful until it is out of step with Jackrabbit, which is
 * still where billing and therefore the authoritative address lives.
 */
/**
 * Her household, on the account screen (D-322).
 *
 * **Was the portal's Account tab, and the tabs are gone** — each one was a
 * destination and now has a URL. This is the one that had no rail item to go
 * to, because Account is not a destination: it is in the header menu for
 * every role already, and D-313 moved billing there for the same reason.
 *
 * So it lands beside the login controls, which is what a parent means by
 * "my account" anyway — her family and the way she signs in, in one place
 * rather than two screens that each hold half of it.
 */
export function HouseholdPanel({ home }: { home: PortalHome }) {
  const { household } = home
  const { session } = useSession()
  const { data: content } = useQuery({ queryKey: ['content'], queryFn: fetchContent })

  /*
   * Her dancers' conversations (D-626): read-only, from here and never mixed
   * into her own list. Offered only where the module is on and the dancer has
   * a login, and the line under it says when her consent does not yet cover
   * messaging (D-629) - the sign-in panel above, on this same screen, is
   * where she agrees. A failed lookup names itself rather than reading as a
   * dancer with no login (D-209).
   */
  const messagingOn = session?.modules?.messaging === true
  const logins = useQuery({ queryKey: ['dancer-logins'], queryFn: fetchDancerLogins, enabled: messagingOn })

  const address = [
    household.addressLine1,
    household.addressLine2,
    [household.city, household.state].filter(Boolean).join(', '),
    household.postalCode,
  ].filter(Boolean)

  return (
    <div className="flex flex-col gap-5">
      <section>
        <h2 className="mb-2 text-[11px] font-bold tracking-[0.11em] text-ink-3 uppercase">
          Your family
        </h2>

        <div className="rounded-2xl border border-line bg-surface px-4 py-3.5">
          <p className="text-[14px] font-semibold">{household.name}</p>

          {address.length > 0 ? (
            <p className="mt-1 text-[13px] leading-relaxed text-ink-2">
              {address.map((line) => (
                <span key={line} className="block">
                  {line}
                </span>
              ))}
            </p>
          ) : (
            <p className="mt-1 text-[13px] text-ink-3">No address on file.</p>
          )}
        </div>
      </section>

      <section>
        <h2 className="mb-2 text-[11px] font-bold tracking-[0.11em] text-ink-3 uppercase">
          Contacts
        </h2>

        <div className="rounded-2xl border border-line bg-surface px-4">
          {home.guardians.map((guardian) => (
            <div key={guardian.name} className="border-b border-line py-3 last:border-b-0">
              <p className="text-[14px] font-semibold">
                {guardian.name}
                {guardian.relationship && (
                  <span className="ml-2 text-[12px] font-normal text-ink-3">
                    {guardian.relationship}
                  </span>
                )}
              </p>

              {guardian.email && <p className="text-[13px] text-ink-2">{guardian.email}</p>}
              {guardian.mobilePhone && <p className="text-[13px] text-ink-2">{guardian.mobilePhone}</p>}
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="mb-2 text-[11px] font-bold tracking-[0.11em] text-ink-3 uppercase">
          Your dancers
        </h2>

        <div className="rounded-2xl border border-line bg-surface px-4">
          {messagingOn && logins.isError && (
            <p className="py-3 text-[12px] text-crit" role="alert">
              {text(content, 'portal.conversations.failed', 'Her conversations could not be loaded. Try again in a moment.')}
            </p>
          )}

          {home.dancers.map((dancer) => {
            const login = logins.data?.find((l) => l.dancerId === dancer.dancerId)
            const on = login?.state === 'active' || login?.state === 'invited'

            return (
              <div
                key={dancer.dancerId}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-line py-3 text-[14px] last:border-b-0"
              >
                <span className="min-w-0 grow">{dancer.name}</span>

                {messagingOn && login && on && (
                  <Link
                    to={`/portal/dancers/${dancer.dancerId}/conversations`}
                    className="min-h-[24px] text-[13px] font-semibold text-accent-2"
                  >
                    {fill(content, 'portal.conversations.heading', { firstName: login.firstName }) ||
                      `${login.firstName}'s conversations`}
                  </Link>
                )}

                {messagingOn && login && on && login.missingTerms.includes('messaging') && (
                  <span className="basis-full text-[12px] text-ink-3">
                    {fill(content, 'portal.conversations.needsConsent', { firstName: login.firstName }) ||
                      `${login.firstName} cannot be messaged until you agree to the messaging terms under Their own sign-in above.`}
                  </span>
                )}
              </div>
            )
          })}
        </div>
      </section>

      {/*
        Billing, which is Jackrabbit's and says so (D-259).

        **Under the dancers rather than beside the address**, because it is a
        way out of this app and not another fact about the family — and the
        paragraph below already prepares a parent for the two systems being
        separate, so this lands next to its own explanation.

        Absent when the block is blank, so a broken or retired link removes the
        section instead of offering a dead one.
      */}
      {/*
        **It is not here any more** (`asked`, 2026-09-17). This panel is inside
        the account page's *Your family* group, which is closed until somebody
        opens it, so the way out to billing was behind a tap nobody knew to make.
        `AccountView` draws it at the foot of the page instead, where it is
        always visible. The paragraph below stays, because it is about the
        details above it.
      */}

      {/*
        Deliberately does not claim "one place". Our record and the studio's
        billing record are separate systems while Jackrabbit owns billing
        (D-111), and telling a family otherwise sets them up to believe a
        changed address reached their invoice when it did not.
      */}
      <p className="rounded-2xl border border-line bg-surface-2 px-4 py-3 text-[12.5px] text-ink-2">
        Something look wrong? Let the studio know and they will put it right. Please tell them
        rather than assuming &mdash; a new address has to reach your billing too.
      </p>

      {/* Shown as coming, not as a dead tab. A button that does nothing
          collects bug reports, and pilot feedback is the point of the pilot. */}
      <p className="flex items-center justify-center gap-2 py-2 text-[12.5px] text-ink-3">
        <MessageCircle size={14} strokeWidth={2} />
        Messaging your studio is coming soon
      </p>

      <p className="flex items-center justify-center gap-1.5 pb-2 text-[12px] text-ink-3">
        <Check size={12} strokeWidth={2.5} />
        You are signed in
      </p>
    </div>
  )
}

/**
 * Every dancer in the household, on one schedule (D-176).
 *
 * **Each dancer is still fetched through her own scoped endpoint** rather than a
 * new household-wide one. Scoping denies by default (D-83), and adding a second
 * route that returns several children at once would be a second thing to get
 * wrong — this merges answers the server already vouched for individually.
 */
function EveryoneSchedule({
  dancers,
  view,
  anchor,
  onView,
  onAnchor,
  onOpenDay,
  onReport,
}: {
  dancers: PortalHome['dancers']
  view: ScheduleView
  anchor: string
  onView: (v: ScheduleView) => void
  onAnchor: (date: string) => void
  /** Opening a day changes the anchor and the view together (D-435). */
  onOpenDay: (date: string) => void
  onReport: (date: string) => void
}) {

  const { from, to, label } = scheduleWindow(view, anchor)

  const results = useQueries({
    queries: dancers.map((d) => ({
      queryKey: ['portal-dancer', d.dancerId, from, to],
      queryFn: () => fetchPortalDancer(d.dancerId, from, to),
    })),
  })

  const loading = results.some((r) => r.isPending)

  /*
   * Whose class it is has to travel with the session, or a merged evening is a
   * list of times a parent cannot act on — the entire point is knowing which
   * child to have in the car.
   */
  const merged = results.flatMap((r, i) =>
    (r.data?.upcoming ?? []).map((s) => ({
      session: s,
      who: dancers[i].firstName,
      // Carried too, because reporting an absence has to name a dancer and a
      // merged evening is several of them.
      dancerId: dancers[i].dancerId,
    })),
  )

  /*
   * Sorted by clock, not by child.
   *
   * The merge produces one dancer's whole week, then the next, so Everly's 5:45
   * landed underneath Ellie's 6:45 and the evening read out of order. A parent
   * scanning this is asking "who is next, and where" — the answer is a time
   * sequence, and grouping it by child is the thing she was already doing in her
   * head (D-176).
   *
   * A class with no start time sorts last: it meets on no dates yet (D-84), so
   * it is not part of tonight's running order.
   */
  const ordered = [...merged].sort((a, b) =>
    a.session.date !== b.session.date
      ? a.session.date.localeCompare(b.session.date)
      : (a.session.startTime ?? '99').localeCompare(b.session.startTime ?? '99'),
  )

  const days = byDate(ordered.map((m) => m.session))
  const owner = new Map(ordered.map((m) => [m.session.occurrenceId, m]))

  return (
    <div className="flex flex-col gap-4">
      {/*
        The same header as the single-dancer schedule and as the teaching half
        (D-359). No `dancer` prop, so no *Ask for a private*: that takes exactly
        one dancer id and Everyone cannot know which child it means (D-348).
      */}
      <ScheduleHeader
        label={label}
        anchor={anchor}
        view={view}
        onPick={onAnchor}
        onView={(next) => isScheduleView(next) && onView(next)}
        onStep={(d) => onAnchor(stepAnchor(view, anchor, d))}
        onToday={() => onAnchor(today())}
        hideTeachers
        actions={<ScheduleControls view={view} onReport={onReport} anchor={anchor} />}
      />

      {loading && <p className="py-6 text-center text-[13px] text-ink-3">Loading...</p>}

      {/* Missing here while the single-dancer schedule had it, so Everyone
          fell through to a flat list of the whole month (D-176). */}
      {view === 'month' && !loading && (
        <MonthGrid
          anchor={anchor}
          sessions={ordered.map((m) => m.session)}
          owners={new Map(ordered.map((m) => [m.session.occurrenceId, m.who]))}
          onPick={onOpenDay}
        />
      )}

      {view !== 'month' && !loading && days.length === 0 && (
        <p className="rounded-2xl border border-line bg-surface px-5 py-8 text-center text-[14px] text-ink-3">
          Nothing for anyone this {view}.
        </p>
      )}

      <div className={view === 'week' ? 'grid gap-4 lg:grid-cols-2 lg:items-start' : 'flex flex-col gap-4'}>
        {view !== 'month' &&
          days.map((day) => (
          <section key={day.date}>
            <h2 className="mb-1.5 text-[11px] font-bold tracking-[0.11em] text-ink-3 uppercase">
              {day.date === today() ? 'Today' : formatFullDate(day.date)}
            </h2>

            <div className="rounded-2xl border border-line bg-surface px-4">
              {day.sessions.map((session) => (
                <SessionRow
                  key={session.occurrenceId}
                  session={session}
                  who={owner.get(session.occurrenceId)?.who}
                  dancerId={owner.get(session.occurrenceId)?.dancerId}
                />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  )
}

/**
 * Week / Day / Month, shared by the one-dancer and whole-household schedules.
 *
 * Extracted rather than duplicated: two copies would drift, and the one that
 * drifted would be the household view nobody tests as often.
 */
/**
 * The controls that sit above a parent's week (D-346).
 *
 * **One line for both branches, because the same screen was answering the same
 * question two ways.** Picking *Everyone* put *Report an absence* beside the
 * week; picking a child folded the identical button inside the *Absences &
 * makeups* drawer. Reporting an absence is an action, and an action a parent
 * has to go looking for is one she does not take.
 *
 * **And the two labels had already drifted**, which is the argument for one
 * component rather than two tidy ones: the Everyone branch spelled it out as a
 * literal in code while the drawer read `portal.actions.report`. Tonya editing
 * her own words would have changed one of them (A-16, D-199).
 *
 * **It knows which day is on screen** — opening it from Tuesday starts on
 * Tuesday, which is what replaced the per-row link (D-177).
 */
/**
 * What a parent *does* to the schedule she is looking at.
 *
 * **It no longer carries the view toggle** (D-359). That went into
 * `ScheduleHeader` with the date controls, which is where the teaching half has
 * always kept it — and the point of D-297 survives the move intact: these are
 * still one control rather than three buttons stacked down the page, and they
 * still sit in the same line as *how you are looking*.
 */
function ScheduleControls({
  view,
  onReport,
  anchor,
  dancer,
  repairable,
}: {
  view: ScheduleView
  onReport: (date: string) => void
  anchor: string
  /**
   * Absent under *Everyone*, and that is a real limit rather than an oversight.
   *
   * `askForPrivate` takes exactly one dancer id, so a control that cannot know
   * which child it means has nothing to send. Reporting an absence has no such
   * problem - it preselects every dancer - which is why one control survives
   * the Everyone branch and the other does not (D-348).
   */
  /**
   * ⚠ `paysFromCredit` is **required inside the object**, not optional (D-836).
   *
   * The object is optional — the *Everyone* header passes no dancer at all — but a
   * caller that does name one must say which family she is. It was optional for
   * about an hour, and in that hour **this call site dropped it**: the two narrow
   * doors into the request form were wired by hand and the wide one, the
   * *Schedule a private* button the bell lands next to, silently got `undefined`
   * and fell to `AskForm`'s hide-the-sentence default. So a credit family read
   * *"we will tell you how she takes payment"* — the exact sentence D-662 exists
   * to stop the app saying to her.
   *
   * **An audit found it by rendering the screen**, which no test of mine could
   * have: all six new ones hand `AskForm` a boolean directly and so prove the
   * branch while the caller is broken. Required here is what stops the next one.
   */
  dancer?: { id: string; name: string; paysFromCredit: boolean }
  repairable?: { occurrenceId: string; className: string; date: string }[]
}) {
  const { data: content } = useQuery({ queryKey: ['content'], queryFn: fetchContent })

  /*
   * ⚠ **This row is the one place the two switches are both asked** (D-920),
   * and it is the reason they are two.
   *
   * *Report an absence* costs nothing; *Schedule a private* commits the
   * household to a paid lesson — `asked`, choosing two switches over one:
   * most parents would let a fourteen-year-old say she is sick without
   * letting her book. So they are gated apart, and the **row** goes only when
   * she has neither, because an empty flex row of controls is a gap that
   * reads as something failing to load.
   */
  const mayReport = useCan('ArrangeAttendance')
  const mayBook = useCan('BookPrivates')
  if (!mayReport && !mayBook) return null

  return (
    // `min-w-0` for the same reason as `ScheduleHeader`'s nav: the ask form
    // opened from here contains a scrolling grid, and an unfloored flex
    // ancestor carries its intrinsic minimum up to the page.
    <div className="flex min-w-0 flex-wrap items-center gap-2">
      {mayReport && (
      <button
        type="button"
        onClick={() => onReport(view === 'month' ? today() : anchor)}
        /*
          `py-1.5 text-[12.5px]`, matching *Schedule a private* beside it and
          not the header's arrows. It was briefly bumped to match the arrows,
          which made this pair 38px and 33px — and `AskForm` cannot follow,
          because it has a second caller (D-199). The owner's complaint one day
          earlier was about exactly these two not lining up (D-358), so the pair
          wins over the row.

          **And pink, like its sibling** (D-500, amending D-383). D-383 reasoned
          this one should stay quiet because *I am out* is quiet on the teaching
          tab. Looking at the shipped screen answered it the other way: *"the
          report an absence should prob be the same hot pink as schedule
          private, otherwise its kind of hidden/blends in"*. The reasoning held
          for a teacher, whose row is her working day; a parent opens this
          screen a handful of times a season, and the two things she came to do
          are these.

          **Outlined rather than filled, and its sibling changed to match.**
          Filling both left the screen with two solid accents plus a solid
          *Today*, which is three — *"i'm good with either one but want to make
          sure its consistent"*. So the pair is one treatment, pink enough that
          neither disappears, and the solid fill is left to mean exactly one
          thing. The two carry identical borders, so they still measure level —
          the alignment fault D-383 found while making this button quiet.
        */
        className="rounded-lg border border-accent px-3 py-1.5 text-[12.5px] font-semibold text-accent hover:bg-accent-soft"
      >
        {text(content, 'portal.actions.report', 'Report an absence')}
      </button>
      )}

      {/* The one that costs money, behind the switch that says so (D-920). */}
      {mayBook && dancer && (
        <AskForm
          dancerId={dancer.id}
          dancerName={dancer.name}
          paysFromCredit={dancer.paysFromCredit}
          repairable={repairable ?? []}
        />
      )}
    </div>
  )
}
